import { useDrawerEscape } from '../../lib/hooks/useDrawerEscape';
import React, { useEffect, useState, useRef } from 'react';
import { useMailProvider } from './MailProviderContext';
import { useAuth } from '../../lib/auth';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { MailSidebar } from './MailSidebar';
import { MailThreadView } from './MailThreadView';
import { MailComposeDrawer } from './MailComposeDrawer';
import { X, Maximize2, Minimize2, Mail } from 'lucide-react';

// Ref-count modulo-level dei drawer mail aperti: Gmail e Outlook montano ciascuno la
// propria istanza di MailDrawer e condividono il lock scroll del body. Nel passaggio
// diretto da un drawer all'altro (mutua esclusione nei wrapper) l'effetto di chiusura
// del primo NON deve rilasciare il lock del secondo appena aperto: si rilascia solo
// quando il contatore torna a zero. Robusto a qualunque ordine di esecuzione degli effect.
let openMailDrawers = 0;

// `enabled` = gate provider-specifico (per Gmail: google_workspace_enabled && sync_gmail),
// calcolato dal wrapper che conosce il provider.
export function MailDrawer({ enabled }: { enabled: boolean }) {
  const { useStore, useDrawerStore, displayName } = useMailProvider();
  const { isOpen, mode, close, setMode } = useDrawerStore();
  useDrawerEscape(isOpen,close,64);

  const { selectedThreadId, composing, viewMode, syncInbox, startPolling, stopPolling, setPollingSpeed, loadTemplates, loadLabels, loadDrafts, clearKeptUnread } = useStore();
  const { user } = useAuth();
  const { settings } = useSettingsStore();

  const [shouldRender, setShouldRender] = useState(false);
  const [animating, setAnimating] = useState(false);
  const initializedRef = useRef(false);

  const organizationId = settings?.organization_id || (user as any)?.user_metadata?.organization_id;
  const userId = user?.id;

  // Pre-load al login (come WhatsApp): sync + polling background indipendente da isOpen.
  // Ritardato di qualche secondo: al login la pagina corrente deve vincere la gara
  // per le connessioni del browser. Se il drawer viene aperto prima, l'init parte
  // subito dal fallback nell'effect di apertura.
  useEffect(() => {
    if (initializedRef.current) return;
    if (!organizationId || !userId) return;
    if (!enabled) return;

    const t = setTimeout(() => {
      if (initializedRef.current) return;
      initializedRef.current = true;
      syncInbox(organizationId, userId, false);
      loadTemplates(organizationId);
      loadLabels(organizationId, userId);
      loadDrafts(organizationId, userId);
      startPolling(organizationId, userId, 300000); // slow polling 5min di default
    }, 8000);
    return () => clearTimeout(t);
  }, [organizationId, userId, enabled]);

  // Lock scroll del body (ref-count tra le istanze Gmail/Outlook: rilascio solo
  // quando l'ultimo drawer aperto si chiude o smonta).
  useEffect(() => {
    if (!isOpen) return;
    openMailDrawers++;
    document.body.style.overflow = 'hidden';
    return () => {
      openMailDrawers = Math.max(0, openMailDrawers - 1);
      if (openMailDrawers === 0) document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Animation lifecycle + fast/slow polling switch
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));

      // Fallback init: se pre-load non è partito (settings non pronte al login,
      // o sync_gmail attivato in questa sessione), inizializza qui al primo open.
      // `enabled` obbligatorio anche qui: senza account connesso partirebbe un
      // sync/polling destinato a fallire per sempre in silenzio.
      if (!initializedRef.current && enabled && organizationId && userId) {
        initializedRef.current = true;
        syncInbox(organizationId, userId, false);
        loadTemplates(organizationId);
        startPolling(organizationId, userId, 15000); // fast subito visto che è aperto
      } else if (initializedRef.current && organizationId && userId) {
        setPollingSpeed(organizationId, userId, 'fast');
      }
    } else {
      setAnimating(false);
      // Drawer chiuso: alla riapertura la vista riparte, le conversazioni lette lasciano "Da leggere"
      clearKeptUnread();
      // Torna a slow polling con drawer chiuso (badge ancora live)
      if (initializedRef.current && organizationId && userId) {
        setPollingSpeed(organizationId, userId, 'slow');
      }
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (initializedRef.current) {
        stopPolling();
      }
    };
  }, []);

  if (!shouldRender || !organizationId || !userId) return null;

  const content = (
    <div className="flex-1 flex overflow-hidden">
      {viewMode === 'list' ? (
        // Vista "righe lunghe": lista a tutta larghezza, thread selezionato a pieno
        <div className="flex-1 h-full min-w-0 overflow-hidden flex flex-col">
          {selectedThreadId ? (
            <MailThreadView organizationId={organizationId} userId={userId} />
          ) : (
            <MailSidebar organizationId={organizationId} userId={userId} />
          )}
        </div>
      ) : (
        <>
          {/* Sidebar */}
          <div className="w-[420px] h-full flex-shrink-0 flex flex-col overflow-hidden border-r border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <MailSidebar organizationId={organizationId} userId={userId} />
          </div>

          {/* Main area */}
          <div className="flex-1 h-full min-w-0 overflow-hidden">
            {selectedThreadId ? (
              <MailThreadView organizationId={organizationId} userId={userId} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Mail className="h-10 w-10 text-[#787774]/40 mx-auto mb-3" />
                  <p className="text-sm text-[#787774] dark:text-white/40">
                    Seleziona un thread per leggere le email
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Compose */}
      {composing && (
        <MailComposeDrawer organizationId={organizationId} userId={userId} />
      )}
    </div>
  );

  const header = (
    <div className="flex items-center justify-between h-12 px-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#202020] flex-shrink-0">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-[#37352F] dark:text-white" />
        <h2 className="text-sm font-semibold text-[#37352F] dark:text-white">{displayName}</h2>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setMode(mode === 'full' ? 'compact' : 'full')}
          className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60 transition-colors"
          title={mode === 'full' ? 'Compatto' : 'Espandi'}
        >
          {mode === 'full' ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
        <button
          onClick={close}
          className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  // Full mode — drawer a tutta altezza (no margini sopra/sotto, non stondato),
  // largo da poco dopo il menu di sinistra fino al bordo destro: resta un drawer, non un modale.
  if (mode === 'full') {
    return (
      <div
        className={`fixed z-[64] flex flex-col bg-white dark:bg-[#191919] border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 shadow-2xl overflow-hidden transition-all duration-250 ease-out ${
          animating ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-3'
        }`}
        style={{
          left: 'calc(var(--sidebar-width, 256px) + 8px)',
          top: '3rem',
          right: 0,
          bottom: 0,
        }}
      >
        {header}
        {content}
      </div>
    );
  }

  // Compact mode
  return (
    <>
      <div
        className={`fixed inset-0 z-[63] bg-black/20 dark:bg-black/40 transition-opacity duration-250 max-md:!left-0 ${
          animating ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ top: '3rem', left: 'var(--sidebar-width, 256px)' }}
        onClick={close}
      />
      <div
        className={`fixed right-0 z-[64] w-[60%] flex flex-col bg-white dark:bg-[#191919] border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 shadow-2xl overflow-hidden transition-transform duration-300 ease-in-out ${
          animating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: '3rem', bottom: 0 }}
      >
        {header}
        {content}
      </div>
    </>
  );
}
